import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { type Api, syncStrava } from "api-client";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { Button, useToast } from "ui";
import { getRelativeTime } from "utils";

import { useAuthStore } from "src/auth/AuthContext";
import { DataTable } from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import { SettingsContextLink } from "src/components/SettingsContextLink";
import { SettingsPage } from "src/components/SettingsPage";
import { StravaActivityDetail } from "src/components/strava/StravaActivityDetail";
import { useTableSearch } from "src/components/TableSearchContext";
import { stravaActivitiesQueryOptions } from "src/queries/integrations";

type StravaActivity = Api.GetStravaActivities.Output.Activity;

function formatDistance(meters: number | null): string {
  if (meters === null) return "-";

  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "-";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) return `${hours}h ${minutes}m`;

  return `${minutes}m`;
}

const STRAVA_TO_WORKOUT_TYPE: Record<string, string> = {
  Run: "RUNNING",
  Ride: "CYCLING",
  Swim: "SWIMMING",
  Walk: "WALKING",
  Hike: "HIKING",
  Yoga: "YOGA",
  Crossfit: "CROSSFIT",
  WeightTraining: "STRENGTH",
  Rowing: "ROWING",
  HIIT: "HIIT",
};
export function StravaActivitiesView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const authStore = useAuthStore();
  const tableSearch = useTableSearch();
  const page = tableSearch.pageIndex + 1;
  const limit = tableSearch.pageSize;
  const activitiesQueryOptions = stravaActivitiesQueryOptions({ page, limit });
  const activitiesQuery = useSuspenseQuery(activitiesQueryOptions);
  const { activities, total } = activitiesQuery.data;

  const columns: ColumnDef<DataTableFeatures, StravaActivity>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: t("integrationsPage.stravaActivities.columns.name"),
      cell: ({ row }) => {
        return <div className="font-medium">{row.original.name}</div>;
      },
    },
    {
      id: "sportType",
      accessorKey: "sportType",
      header: t("integrationsPage.stravaActivities.columns.type"),
      cell: ({ row }) => {
        return (
          <span className="text-xs px-2 py-1 bg-muted rounded">
            {row.original.sportType}
          </span>
        );
      },
    },
    {
      id: "startDate",
      accessorKey: "startDate",
      header: t("integrationsPage.stravaActivities.columns.date"),
      cell: ({ row }) => {
        return (
          <div className="text-muted-foreground">
            {t(
              "relativeTime",
              getRelativeTime(new Date(row.original.startDate)),
            )}
          </div>
        );
      },
    },
    {
      id: "distance",
      accessorKey: "distance",
      header: t("integrationsPage.stravaActivities.columns.distance"),
      cell: ({ row }) => {
        return <div>{formatDistance(row.original.distance)}</div>;
      },
    },
    {
      id: "movingTime",
      accessorKey: "movingTime",
      header: t("integrationsPage.stravaActivities.columns.duration"),
      cell: ({ row }) => {
        return <div>{formatDuration(row.original.movingTime)}</div>;
      },
    },
    {
      id: "calories",
      accessorKey: "calories",
      header: t("integrationsPage.stravaActivities.columns.calories"),
      cell: ({ row }) => {
        return <div>{row.original.calories ?? "-"}</div>;
      },
    },
    {
      id: "workoutId",
      accessorKey: "workoutId",
      header: t("integrationsPage.stravaActivities.columns.workout"),
      cell: ({ row }) => {
        if (row.original.workoutId) {
          return (
            <Icon name="tabler-check" className="size-4 text-emerald-600" />
          );
        }

        return null;
      },
    },
  ];

  const syncMutation = useMutation({
    mutationFn: () => {
      return syncStrava({ body: {} });
    },
    onSuccess: () => {
      toast.toast({
        title: t("integrationsPage.strava.syncStarted"),
        description: t("integrationsPage.strava.syncStartedDescription"),
        type: "background",
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: activitiesQueryOptions.queryKey,
      });
    },
    onError: () => {
      toast.toast({
        title: t("integrationsPage.strava.syncError"),
        type: "background",
        variant: "destructive",
      });
    },
  });

  return (
    <SettingsPage fullWidth>
      <div className="space-y-4">
        <SettingsContextLink
          destination="strava"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <Icon name="tabler-arrow-left" className="size-4" />
          {t("integrationsPage.stravaActivities.backToStrava")}
        </SettingsContextLink>

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {t("integrationsPage.stravaActivities.title")}
          </h2>
          {authStore.isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              loading={syncMutation.isPending}
              onClick={() => {
                syncMutation.mutate();
              }}
            >
              <Icon name="tabler-refresh" className="size-4 mr-2" />
              {t("integrationsPage.stravaActivities.syncNow")}
            </Button>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          data={activities}
          getRowId={(row) => {
            return row.id;
          }}
          hideToolbar
          manualPagination
          rowCount={total}
          pagination={{ pageIndex: page - 1, pageSize: limit }}
          onPaginationChange={tableSearch.onPaginationChange}
          expandMode="single"
          renderExpandedRow={(row) => {
            const workoutType =
              STRAVA_TO_WORKOUT_TYPE[row.original.type] ?? "OTHER";
            return (
              <div className="p-3">
                <StravaActivityDetail
                  stravaActivityId={row.original.id}
                  workoutType={workoutType}
                />
              </div>
            );
          }}
        />

        <div className="flex justify-end mt-4">
          <img
            src="/assets/api_logo_pwrdBy_strava_horiz_orange.svg"
            alt={t("integrationsPage.stravaActivities.poweredByStrava")}
            className="h-8"
          />
        </div>
      </div>
    </SettingsPage>
  );
}
